import { Card, Flex, Group, Loader, Space, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getConvitesByQuestionario } from "@/modules/questionario-colaborador/firestore";
import { getRepostasQuestionarioArray } from "@/modules/responder-questionario/firestore";
import useUserStore from "@/modules/users/store";
import { CINCO, DEZ, MIL, SESSENTA } from "@/utils/constants";

const stateTime = CINCO * SESSENTA * MIL; // 5 minutos
const gcTime = DEZ * SESSENTA * MIL; // 10 minutos

const removeDuplicates = (data: TConvite[]) => {
  const uniqueIds = new Set();
  return data.filter((item) => {
    if (!uniqueIds.has(item.idColaboradorConvidado)) {
      uniqueIds.add(item.idColaboradorConvidado);
      return true;
    }
    return false;
  });
};

const CardEmpresa = ({ empresas, questionario }: { empresas: TConvite[]; questionario: string }) => {
  const empresaAtivaId = useUserStore((state) => state.activeEmpresaId);
  const isSmMarches = useMediaQuery("(max-width: 48em)");

  const uniqueArray = useMemo(
    () => empresas.filter((item, index, self) => index === self.findIndex((t) => t.idEmpresa === item.idEmpresa)),
    [empresas]
  );

  const fetchTotalColaboradores = async (): Promise<number> => {
    if (!empresas?.length || !empresaAtivaId || !questionario) return 0;

    const idsEmpresasInteresse = new Set([
      ...uniqueArray.map((item) => item.idEmpresa),
      ...empresas.map((item) => item.idEmpresaColaboradorConvidado)
    ]);

    // Busca todos os dados de uma vez em paralelo
    // eslint-disable-next-line compat/compat
    const [convitesSnap, respostasSnap] = await Promise.all([
      getConvitesByQuestionario(questionario),
      getRepostasQuestionarioArray(empresaAtivaId, questionario)
    ]);

    const allConvites = convitesSnap.docs
      .map((d) => d.data())
      .filter((c) => idsEmpresasInteresse.has(c.idEmpresa) && !c.cancelado);

    const allRespostas = respostasSnap.docs
      .map((d) => d.data())
      .filter((r) => idsEmpresasInteresse.has(r.idEmpresa) && r.idRespondente === "" && !r.pausado);

    // Contagem global respeitando a lógica de duplicados por empresa
    let total = 0;
    for (const idEmpresa of idsEmpresasInteresse) {
      const convitesEmpresa = allConvites.filter((c) => c.idEmpresa === idEmpresa);
      const respostasEmpresa = allRespostas.filter((r) => r.idEmpresa === idEmpresa);
      total += removeDuplicates(convitesEmpresa).length + respostasEmpresa.length;
    }

    return total;
  };

  const { data: totalColaboradores = 0, isLoading } = useQuery({
    queryKey: ["total-colaboradores-relatorio", empresaAtivaId, questionario, uniqueArray],
    queryFn: fetchTotalColaboradores,
    enabled: Boolean(empresas?.length && empresaAtivaId && questionario),
    staleTime: stateTime,
    gcTime: gcTime
  });

  const totalEmpresas = uniqueArray.length;

  return (
    <Group justify={"space-around"}>
      <Card style={{ width: isSmMarches ? "100%" : "30%", height: "150px", display: "flex" }} withBorder>
        <Flex align={"center"} direction="column">
          <Text size="xl" fw={700}>
            Empresas inscritas
          </Text>
          <Space h={"md"} />
          <Text size="xl" fw={500}>
            {totalEmpresas}
          </Text>
        </Flex>
      </Card>
      <Card style={{ width: isSmMarches ? "100%" : "30%", height: "150px" }} withBorder>
        <Flex align={"center"} direction="column">
          <Text size="xl" fw={700}>
            Colaboradores Convidados
          </Text>
          <Space h={"md"} />
          {isLoading ? (
            <Group align={"center"} justify={"center"}>
              <Loader color="blue" />
            </Group>
          ) : (
            <Text size="xl" fw={500}>
              {totalColaboradores}
            </Text>
          )}
        </Flex>
      </Card>
    </Group>
  );
};

export default CardEmpresa;
